import { useRef, useMemo, useState, cloneElement, isValidElement } from "react"
import { DateTime } from "luxon"
import { useId } from "@floating-ui/react"
import { useCalendarState } from "react-stately"
import { CalendarDate, createCalendar, today } from "@internationalized/date"
import { Icon, Popover, TextInput, FocusManager } from "../.."
import { DatePickerCalendar } from "./DatePickerCalendar"
import { getAriaCalendarProps } from "./getAriaCalendarProps"
import { DEFAULT_LOCALE, DEFAULT_TIME_ZONE } from "./constants"
import { isValidTimeZone, fromInternationalizedDateToLuxonDate } from "./utils"
import { DatePickerProps } from "./types"

export function DatePicker({
  changeDateButtonLabel = "Change Date",
  chooseDateButtonLabel = "Choose Date",
  defaultValue,
  disableFutureDates = false,
  disablePastDates = false,
  disableSpecificDates,
  error,
  id: idProp,
  isDateDisabled,
  label: labelProp,
  maxValue = DateTime.local(2049).endOf("year"),
  minValue = DateTime.local(1950).startOf("year"),
  monthSelectLabelText = "Month",
  name,
  onBlur,
  onCalendarClose,
  onCalendarOpen,
  onChange: onChangeProp,
  onFocus,
  onMonthChange,
  onYearChange,
  selectedDateDescription: getSelectedDateDescription = value => {
    return value.toLocaleString(DateTime.DATE_HUGE, { locale: DEFAULT_LOCALE })
  },
  showErrorMessage = true,
  size = "md",
  timeZone: timeZoneProp = DEFAULT_TIME_ZONE,
  value,
  yearSelectLabelText = "Year",
  disabled,
  clearable = false,
}: DatePickerProps) {
  const locale = DEFAULT_LOCALE
  const timeZone = isValidTimeZone(timeZoneProp) ? timeZoneProp : DEFAULT_TIME_ZONE

  const [showCalendar, setShowCalendar] = useState(false)

  const ariaCalendarProps = getAriaCalendarProps({
    timeZone,
    value,
    defaultValue,
    minValue,
    maxValue,
    disablePastDates,
    disableFutureDates,
    disableSpecificDates,
    isDateDisabled,
    onChange: onChangeProp,
  })

  const inputRef = useRef<HTMLInputElement>(null)
  const calendarToggleButtonRef = useRef<HTMLButtonElement>(null)

  const initialFocusedDate = useRef<CalendarDate>(
    ariaCalendarProps.value || ariaCalendarProps.defaultValue || today(timeZone),
  )

  const calendarState = useCalendarState({
    locale,
    createCalendar,
    ...ariaCalendarProps,
    onFocusChange(date) {
      if (date.month !== initialFocusedDate.current.month) {
        onMonthChange?.(date.month)
      }

      if (date.year !== initialFocusedDate.current.year) {
        onYearChange?.(date.year)
      }

      initialFocusedDate.current = date
    },
    onChange(value) {
      ariaCalendarProps.onChange?.(value)
      setShowCalendar(false)
    },
  })

  const inputValue = calendarState.value
    ? DateTime.fromISO(calendarState.value.toString(), {
        zone: timeZone,
        locale,
      }).toLocaleString()
    : ""

  const uniqueId = useId()
  const inputId = idProp || uniqueId

  const label = useMemo(() => {
    if (labelProp && isValidElement(labelProp)) {
      return cloneElement(labelProp, {
        id: name && `${name}-label`,
        htmlFor: labelProp.props.htmlFor || inputId,
        ...labelProp.props,
      })
    }

    return labelProp
  }, [inputId, name, labelProp])

  const isInputMD = size === "md"
  const dateFormatDescriptionElemId = `date-format-desc-${inputId}`
  const iconButtonClassName =
    "outline-black focus-within:outline-black focus:outline-black focus-visible:outline-black"

  const selectedDateDescription = useMemo(() => {
    const value = calendarState.value
      ? fromInternationalizedDateToLuxonDate(calendarState.value, timeZone)
      : undefined

    return value ? getSelectedDateDescription(value) : ""
  }, [calendarState.value, getSelectedDateDescription, timeZone])

  const calendarDialog = (
    <Popover
      offset={12}
      placement="bottom-start"
      open={showCalendar}
      onOpenChange={open => {
        const dateInLuxon = calendarState.value
          ? fromInternationalizedDateToLuxonDate(calendarState.value, timeZone)
          : null

        if (open === true) {
          inputRef.current?.focus()
          onCalendarOpen?.(dateInLuxon)
        } else {
          inputRef.current?.blur()
          onCalendarClose?.(dateInLuxon)
          calendarState.setFocusedDate(calendarState.value || today(timeZone))
        }

        setShowCalendar(open)
      }}
    >
      <Popover.Trigger>
        <button
          type="button"
          ref={calendarToggleButtonRef}
          className={iconButtonClassName}
          aria-label={
            calendarState.value
              ? `${changeDateButtonLabel}, ${selectedDateDescription}`
              : chooseDateButtonLabel
          }
          onClick={() => setShowCalendar(prev => !prev)}
          disabled={disabled}
        >
          <Icon name="CalendarBlank" aria-hidden="true" size={isInputMD ? "sm" : "xs"} />
        </button>
      </Popover.Trigger>

      <Popover.Content role="dialog" aria-modal="true" aria-label={chooseDateButtonLabel}>
        <FocusManager
          // According to WAI-ARIA pattern, a day cell should receive the initial focus.
          // So, use 2 here to set initial focus on the cell with tabIndex=0 (current date/selected date).
          // Using 0 or 1 would set initial focus on the month or year select respectively.
          initialFocus={2}
          isOpen={showCalendar}
          returnFocusRef={calendarToggleButtonRef}
          render={({ getRenderProps }) => {
            return (
              <div {...getRenderProps()}>
                <DatePickerCalendar
                  locale={locale}
                  timeZone={timeZone}
                  minSelectableYear={minValue.year}
                  maxSelectableYear={maxValue.year}
                  monthSelectLabelText={monthSelectLabelText}
                  yearSelectLabelText={yearSelectLabelText}
                  calendarState={calendarState}
                  ariaCalendarProps={ariaCalendarProps}
                />
              </div>
            )
          }}
        />
      </Popover.Content>
    </Popover>
  )

  const endAdornment = useMemo(() => {
    if (!clearable) return null
    if (!calendarState.value) return null

    return (
      <button type="button" onClick={() => calendarState.setValue(null)} aria-label="Clear date">
        {<Icon name="XCircle" size={isInputMD ? "sm" : "xs"} />}
      </button>
    )
  }, [clearable, isInputMD, calendarState])

  return (
    <div onFocus={onFocus} onBlur={onBlur}>
      <TextInput
        readOnly
        aria-describedby={dateFormatDescriptionElemId}
        ref={inputRef}
        id={inputId}
        name={name}
        size={size}
        label={label}
        error={error}
        showErrorMessage={showErrorMessage}
        placeholder="__/__/_____"
        value={inputValue}
        onClick={() => setShowCalendar(true)}
        startAdornment={calendarDialog}
        disabled={disabled}
        endAdornment={endAdornment}
      />
      <p id={dateFormatDescriptionElemId} className="sr-only">
        Date format: dd/mm/yyyy
      </p>
    </div>
  )
}

DatePicker.displayName = "DatePicker"
